import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

import { Input } from './input';

export interface SelectOption {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
    /** Shown after the label (e.g. link/icon); must call `stopPropagation` so the row is not selected as a value. */
    trailing?: React.ReactNode;
    /** When `searchable` is true, used for substring matching (defaults to string `label`, then `value`). */
    searchText?: string;
}

function optionFilterText(option: SelectOption): string {
    if (option.searchText !== undefined) {
        return option.searchText;
    }
    if (typeof option.label === 'string') {
        return option.label;
    }
    return option.value;
}

function optionMatchesFilter(option: SelectOption, query: string): boolean {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
        return true;
    }
    return optionFilterText(option).toLowerCase().includes(trimmed);
}

export interface SelectProps {
    ariaLabel: string;
    options: SelectOption[];
    value: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    /** Adds a filter field above the list (client-side match on `searchText` / label / value). */
    searchable?: boolean;
    searchPlaceholder?: string;
    /** Accessible name for the search field (defaults to `${ariaLabel} search`). */
    searchAriaLabel?: string;
    onBlur?: () => void;
}

export function Select({
    ariaLabel,
    options,
    value,
    onValueChange,
    placeholder = 'Select an option',
    disabled = false,
    className,
    searchable = false,
    searchPlaceholder = 'Search…',
    searchAriaLabel,
    onBlur,
}: SelectProps) {
    const [open, setOpen] = React.useState(false);
    const [filterQuery, setFilterQuery] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const listboxId = React.useId();
    const selectedOption = options.find((option) => option.value === value);

    const filteredOptions = React.useMemo(
        () =>
            searchable
                ? options.filter((option) => optionMatchesFilter(option, filterQuery))
                : options,
        [filterQuery, options, searchable],
    );

    React.useEffect(() => {
        if (!open) {
            setFilterQuery('');
        }
    }, [open]);

    React.useEffect(() => {
        if (!open || !searchable) {
            return;
        }
        const id = requestAnimationFrame(() => searchInputRef.current?.focus());
        return () => cancelAnimationFrame(id);
    }, [open, searchable]);

    React.useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                role="combobox"
                aria-label={ariaLabel}
                aria-controls={listboxId}
                aria-expanded={open}
                aria-haspopup="listbox"
                disabled={disabled}
                className={cn(
                    'flex h-9 w-full items-center justify-between gap-2 rounded-md border-0 admin-field-fill px-3 text-left text-sm font-medium text-neutral-950 shadow-none transition-colors',
                    'hover:bg-[var(--admin-field-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-0',
                    'disabled:cursor-not-allowed disabled:text-neutral-500 disabled:opacity-70',
                    'dark:text-neutral-100 dark:disabled:text-neutral-400',
                    className,
                )}
                onClick={() => setOpen((current) => !current)}
                onBlur={() => onBlur?.()}
            >
                <span className={cn('truncate', !selectedOption && 'text-neutral-500')}>
                    {selectedOption?.label ?? placeholder}
                </span>
                <ChevronDown
                    aria-hidden="true"
                    className={cn(
                        'size-4 shrink-0 text-neutral-500 transition-transform',
                        open && 'rotate-180',
                    )}
                />
            </button>
            {open && (
                <div
                    className="absolute z-50 mt-1 max-h-72 w-full overflow-hidden rounded-md border border-neutral-200 bg-white text-sm font-medium text-neutral-950 shadow-lg ring-1 ring-black/5 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-50"
                    role="listbox"
                    id={listboxId}
                    aria-label={ariaLabel}
                >
                    {searchable ? (
                        <div
                            className="border-b border-neutral-200 p-2 dark:border-neutral-700"
                            onPointerDown={(event) => event.stopPropagation()}
                        >
                            <Input
                                ref={searchInputRef}
                                variant="outlined"
                                aria-label={searchAriaLabel ?? `${ariaLabel} search`}
                                placeholder={searchPlaceholder}
                                value={filterQuery}
                                onChange={(event) => setFilterQuery(event.target.value)}
                                className="text-sm"
                            />
                        </div>
                    ) : null}
                    <div className="max-h-60 overflow-y-auto py-1">
                        {filteredOptions.length === 0 ? (
                            <p className="px-3 py-2 text-sm font-normal text-neutral-500">
                                No matches.
                            </p>
                        ) : (
                            filteredOptions.map((option) => {
                                const selected = option.value === value;

                                return (
                                    <div
                                        key={option.value}
                                        className={cn(
                                            'flex w-full items-stretch text-neutral-950 transition-colors dark:text-neutral-50',
                                            selected &&
                                                'bg-neutral-100 font-semibold dark:bg-neutral-800',
                                            !selected &&
                                                'hover:bg-neutral-50 dark:hover:bg-neutral-900',
                                        )}
                                    >
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={selected}
                                            disabled={option.disabled}
                                            className={cn(
                                                'flex min-w-0 flex-1 items-center justify-between gap-2 px-3 py-2 text-left',
                                                'disabled:cursor-not-allowed disabled:text-neutral-400',
                                            )}
                                            onClick={() => {
                                                if (option.disabled) return;
                                                onValueChange(option.value);
                                                setOpen(false);
                                            }}
                                        >
                                            <span className="truncate">{option.label}</span>
                                            {selected && (
                                                <Check
                                                    aria-hidden="true"
                                                    className="size-4 shrink-0 text-neutral-700 dark:text-neutral-200"
                                                />
                                            )}
                                        </button>
                                        {option.trailing ? (
                                            <div className="flex shrink-0 items-center border-l border-neutral-200 dark:border-neutral-700">
                                                {option.trailing}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
